import * as React from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * A text input with a label that is actually ATTACHED to it.
 *
 * `components/ui/field.tsx` renders its `<label>` as a SIBLING of whatever it
 * wraps, with no `htmlFor` — so 312 controls across this app have a visible
 * label that no assistive technology associates with the field, and clicking the
 * word does not focus the input. Fixing the shared component means threading an
 * id into every child it has ever been given, which is a repo-wide change and
 * not this one's to make; but the signup form is the first screen a stranger
 * meets, and it is not going to ADD to that count.
 *
 * So: `useId()` for a collision-free id, `htmlFor` on the label, and the hint
 * and error wired through `aria-describedby` so a screen reader reads the rule
 * and the refusal as part of the field rather than as loose text. `aria-invalid`
 * marks the field itself, because a red border is not information.
 *
 * `hint` and `error` share a slot — a field showing both would move the layout
 * on every keystroke — and the error wins, since a rule you have already broken
 * is less useful than the reason you broke it.
 */
export const AuthField = React.forwardRef<
  HTMLInputElement,
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'id'> & {
    label: string;
    hint?: string;
    error?: string | null;
    /** Rendered next to the label, for the one field that is not required. */
    optionalLabel?: string;
    /** A control drawn inside the input's right edge — the password reveal. */
    adornment?: React.ReactNode;
  }
>(({ label, hint, error, optionalLabel, adornment, className, ...props }, ref) => {
  const id = React.useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  return (
    <div className="space-y-1.5">
      {/*
        The "Optional" marker is a SIBLING of the label, not a child of it. Inside
        the element, it becomes part of the field's accessible name — a screen
        reader announcing "Last name Optional" and, more concretely, a name that
        no longer matches the visible label. Outside, the label names the field
        and the marker is what it is: a note beside it.
      */}
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {optionalLabel ? (
          <span className="text-xs text-ink-muted">
            {optionalLabel}
          </span>
        ) : null}
      </div>

      <div className="relative">
        <Input
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          className={cn(
            error && 'border-red-500 dark:border-red-500',
            // Space for the adornment on whichever side the reading direction
            // puts "the end" — `pe-*` is the logical property, so this is
            // correct in Arabic without a second rule.
            adornment && 'pe-11',
            className,
          )}
          {...props}
        />
        {adornment ? (
          <div className="absolute inset-y-0 end-0 flex items-center pe-1">{adornment}</div>
        ) : null}
      </div>

      {error ? (
        <p id={errorId} className="text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
AuthField.displayName = 'AuthField';
